import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  getContentByType,
  getDefaultDraftIdeasImages,
  getDefaultPunishmentsImages,
  updatePunishmentOrder,
} from "../services/firebase";
import { ContentItem } from "../types";
import { useAuth } from "../contexts/AuthContext";
import Footer from "../components/Footer";
import {
  ArrowUp,
  ArrowDown,
  Search,
  ArrowUpRight,
  ChevronDown,
} from "lucide-react";
import Navbar from "../components/Navbar";
import StyledLoadingSpinner from "../components/ui/StyledLoadingSpinner";
import MetaHead from "../components/ui/MetaHead";
import wheelOfShame from "../assets/wheelofshame.svg";
import wheelOfShamePointer from "../assets/wheelofshamePointer.svg";
import { trackEvent } from "../analyticsConfig";
import FavouritePunishment from "../components/ToggleFavouritePunishment";
import SearchPanel from "../components/SearchPanel/SearchPanel";
import AdUnit from "../components/utils/adUnit";
import { FL_RATING_OPTIONS } from "../constants/flRatingOptions";
//import defaultCardImage from "../assets/PunishmentDefault.png";
import HoverVideoPlayer from "react-hover-video-player";
import { css, keyframes } from "@emotion/css";
import { set } from "lodash";

const explorePageMeta = {
  title: "Browse Fantasy Football Punishments & Last Place Ideas",
  description:
    "Discover the best fantasy football punishments and last place ideas for your league. Browse our collection of hilarious, memorable, and creative punishments that will make losing unforgettable. From tattoos to challenges, find the perfect punishment for your fantasy league losers.",
  keywords: [
    "fantasy football punishments",
    "best fantasy football punishments",
    "last place fantasy football punishments",
    "fantasy football loser punishments",
    "funny fantasy football punishments",
    "fantasy punishment ideas",
    "last place punishments",
    "fantasy football loser ideas",
    "creative fantasy punishments",
    "worst fantasy football punishments",
    "punishment suggestions",
    "fantasy sports consequences",
    "league punishment ideas",
    "fantasy football forfeits",
    "loser challenges fantasy football",
  ],
};

interface FLRatingOption {
  value: string;
  label: string;
  level: number;
}

// Helper Functions
const extractFLRatingLevel = (rating: string): number => {
  const match = rating.match(/Level\s*(\d+)/i);
  return match ? parseInt(match[1], 10) : 0;
};

const ViewToggle: React.FC<{
  viewMode: "grid" | "list";
  setViewMode: (mode: "grid" | "list") => void;
}> = ({ viewMode, setViewMode }) => (
  <div className="flex items-center gap-2 bg-[#1A1A1A] rounded-lg p-1">
    <button
      onClick={() => setViewMode("grid")}
      className={`p-2 rounded-lg transition-colors ${
        viewMode === "grid" ? "bg-[#18f7b1]" : "hover:bg-white/5"
      }`}
    >
      <div className="grid grid-cols-2 gap-0.5 w-5 h-5">
        <div className={`${viewMode === "grid" ? "bg-black" : "bg-white"}`} />
        <div className={`${viewMode === "grid" ? "bg-black" : "bg-white"}`} />
        <div className={`${viewMode === "grid" ? "bg-black" : "bg-white"}`} />
        <div className={`${viewMode === "grid" ? "bg-black" : "bg-white"}`} />
      </div>
    </button>
    <button
      onClick={() => setViewMode("list")}
      className={`p-2 rounded-lg transition-colors ${
        viewMode === "list" ? "bg-[#18f7b1]" : "hover:bg-white/5"
      }`}
    >
      <div className="flex flex-col gap-0.5 w-5 h-5">
        <div
          className={`h-1.5 ${viewMode === "list" ? "bg-black" : "bg-white"}`}
        />
        <div
          className={`h-1.5 ${viewMode === "list" ? "bg-black" : "bg-white"}`}
        />
        <div
          className={`h-1.5 ${viewMode === "list" ? "bg-black" : "bg-white"}`}
        />
      </div>
    </button>
  </div>
);

const DraftIdeas: React.FC = () => {
  // const [punishments, setPunishments] = useState<ContentItem[]>([]);
  const [draftIdeas, setDraftIdeas] = useState<ContentItem[]>([]);
  // const [filteredPunishments, setFilteredPunishments] = useState<ContentItem[]>(
  //   []
  // );
  const [filteredDraftIdeas, setFilteredDraftIdeas] = useState<ContentItem[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  // const [embarrassmentLevel, setEmbarrassmentLevel] = useState(0);
  const [selectedFLRating, setSelectedFLRating] = useState<string>("");
  const [isReordering, setIsReordering] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [displayCount, setDisplayCount] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isLevelMenuOpen, setIsLevelMenuOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const [isListView, setIsListView] = useState(false);
  const [isSwitchingView, setIsSwitchingView] = useState(false);
  const [embarrassmentLevel, setEmbarrassmentLevel] = useState(0);
  const [riskLevel, setRiskLevel] = useState(0);
  const [longTermEffects, setLongTermEffects] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [defaultCardImage, setDefaultCardImage] = useState<string>("");
  const [defaultCardVideo, setDefaultCardVideo] = useState<string>("");

  const handleViewChange = (viewType: "grid" | "list"): void => {
    setIsSwitchingView(true); // Show loader
    setTimeout(() => {
      setIsListView(viewType === "list");
      setIsSwitchingView(false); // Hide loader
    }, 300);
  };

  useEffect(() => {
    // Only setup observer if we have more items to load
    if (filteredDraftIdeas.length <= displayCount) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Check if loader is visible and we're not already loading
        const target = entries[0];
        if (target.isIntersecting && !isLoadingMore) {
          setIsLoadingMore(true);
          // Small delay to prevent rapid multiple loads
          setTimeout(() => {
            setDisplayCount((prev) => prev + 12); // Load 12 more items
            setIsLoadingMore(false);
          }, 500);
        }
      },
      {
        root: null, // Use viewport as root
        rootMargin: "100px", // Start loading before reaching the end
        threshold: 0.1, // Trigger when even 10% of the loader is visible
      }
    );

    // Start observing the loader element
    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    // Cleanup observer on unmount
    return () => observer.disconnect();
  }, [
    filteredDraftIdeas.length,
    displayCount,
    isLoadingMore,
    isSwitchingView,
    filteredCount,
    isFilterApplied,
    isReordering,
  ]);

  useEffect(() => {
    const fetchDraftIdeas = async () => {
      try {
        const content = await getContentByType("draftIdeas");
        setDraftIdeas(content);
        setFilteredDraftIdeas(content);

        const tags = Array.from(
          new Set(content.flatMap((item) => item.tags || []))
        );
        setAvailableTags(tags);
      } catch (error) {
        console.error("Error fetching draft ideas:", error);
      }
    };
    const fetchDefaultCardImage = async () => {
      const data = await getDefaultDraftIdeasImages();
      if (data.length > 0) {
        setDefaultCardImage(data[0].cardImageUrl || "");
        setDefaultCardVideo(data[0].cardVideoUrl || "");
      } else {
        console.warn("No default punishment images found.");
      }
    };
    fetchDefaultCardImage();
    fetchDraftIdeas();
  }, []);

  useEffect(() => {
    if (filterRef) {
      window.addEventListener("scroll", handleScroll);
    }
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const filtered = draftIdeas.filter((item) => {
      const selectedLevel =
        FL_RATING_OPTIONS.find((option) => option.value === selectedFLRating)
          ?.level || 0;

      const itemLevel = extractFLRatingLevel(item.flRating || "");

      const matchesSearch = item.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesTags =
        selectedTags.length === 0 ||
        selectedTags.every((tag) => item.tags?.includes(tag));
      const matchesEmbarrasment =
        embarrassmentLevel === 0 ||
        parseInt(item.embarrassmentLevel) === embarrassmentLevel;
      const matchesRisk =
        riskLevel === 0 || parseInt(item.riskLevel) === riskLevel;
      const matchesLongTerm =
        longTermEffects === 0 ||
        parseInt(item.longTermEffects) === longTermEffects;
      const matchesFLRating =
        selectedFLRating === "" || itemLevel === selectedLevel;

      const noFiltersActive =
        embarrassmentLevel === 0 &&
        riskLevel === 0 &&
        longTermEffects === 0 &&
        selectedFLRating === "" &&
        searchTerm.trim() === "";

      return (
        noFiltersActive ||
        (matchesSearch &&
          matchesTags &&
          matchesEmbarrasment &&
          matchesRisk &&
          matchesLongTerm &&
          matchesFLRating)
      );
    });
    setFilteredDraftIdeas(filtered);
    setFilteredCount(filtered.length);
  }, [
    searchTerm,
    selectedTags,
    embarrassmentLevel,
    riskLevel,
    longTermEffects,
    selectedFLRating,
    draftIdeas,
    isFilterApplied,
  ]);

  const handleCardClick = (item: ContentItem) => {
    trackEvent("click", {
      path: item.slug,
      label: item.title,
      id: item.id,
      timestamp: new Date().toISOString(),
    });

    // Delay navigation slightly to allow analytics to queue the event
    setTimeout(() => {
      // Navigate after logging
      navigate(`/draft-ideas/${item.slug}`);
      // console.log("draft ideas card clicked!", item);
    }, 100);
  };

  const handleClickWheelOfShame = () => {
    trackEvent("click", {
      path: "/wheelofshame",
      label: "Wheel of Shame",
      id: null,
      timestamp: new Date().toISOString(),
    });

    // Delay navigation slightly to allow analytics to queue the event
    setTimeout(() => {
      // Navigate after logging
      navigate("/wheelofshame");
    }, 100);
  };

  const handleScroll = () => {
    if (window.scrollY > 220) {
      filterRef?.current?.classList.add(
        "fixed",
        "top-[50px]",
        "sm:top-[80px]",
        "left-0",
        "right-0",
        "bg-white",
        "pt-5",
        "pr-5",
        "pl-5",
        "z-10",
        "shadow-md"
      );
    } else {
      filterRef?.current?.classList.remove(
        "fixed",
        "top-[50px]",
        "sm:top-[80px]",
        "left-0",
        "right-0",
        "bg-white",
        "pt-5",
        "pr-5",
        "pl-5",
        "z-10",
        "shadow-md"
      );
    }
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleReorder = async (itemId: string, direction: "up" | "down") => {
    const currentIndex = filteredDraftIdeas.findIndex(
      (item) => item.id === itemId
    );
    if (currentIndex === -1) return;

    const newDraftIdeas = [...filteredDraftIdeas];
    const item = newDraftIdeas[currentIndex];
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= newDraftIdeas.length) return;

    newDraftIdeas.splice(currentIndex, 1);
    newDraftIdeas.splice(newIndex, 0, item);

    const updatedDraftIdeas = newDraftIdeas.map((item, index) => ({
      ...item,
      order: index,
    }));

    setFilteredDraftIdeas(updatedDraftIdeas);
  };

  const handleReorderButtonClick = async () => {
    if (isReordering) {
      // When finishing reorder, save the new order to the database
      const updatedDraftIdeas = filteredDraftIdeas.map((item, index) => ({
        ...item,
        order: index,
      }));

      try {
        setIsSavingOrder(true);
        await Promise.all(
          updatedDraftIdeas.map((item) =>
            updatePunishmentOrder(item.id, item.order, item.type)
          )
        );
      } catch (error) {
        console.error("Failed to update order:", error);
      } finally {
        setIsSavingOrder(false);
      }
    }

    setIsReordering(!isReordering);
  };

  const extractLevel = (flRating: string | undefined): number => {
    if (!flRating) return 1;

    // Check for format like "Cute & Fluffy (Level 1)"
    const levelMatch = flRating.match(/Level\s*(\d+)/i);
    if (levelMatch) {
      return parseInt(levelMatch[1]);
    }

    // Fallback check for format like "L1"
    const shortMatch = flRating.match(/L(\d+)/i);
    if (shortMatch) {
      return parseInt(shortMatch[1]);
    }

    return 1; // Default to level 1 if no match found
  };

  const extractPosition = (order: number) => {
    const itemOrder = order;
    if (itemOrder >= 0 && itemOrder < 25) {
      if (order < 10) {
        return "top_10";
      } else {
        return "top_25";
      }
    } else {
      return "";
    }
  };

  // Keyframes for loading spinner animation
  const loadingOverlaySpinnerAnimation = keyframes`
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
      `;

  const renderVideoBlock = (content: ContentItem) => {
    return (
      <HoverVideoPlayer
        key={`${content.id}-card-player-${content.videoUrl}`} // Unique key for each video player
        pausedOverlayWrapperClassName="paused-overlay-card-wrapper" // Applies a custom class to the div wrapping the pausedOverlay contents
        hoverOverlayWrapperClassName="hover-overlay-card-wrapper" // Applies a custom class to the div wrapping the hoverOverlay contents
        videoClassName="card-player-video" // Applies a custom class to the video element
        videoId={content.id} // Applies an id to the video element
        videoSrc={content.IsdefaultVideo ? defaultCardVideo : content.videoUrl}
        //style={{ width: "295px", height: "360px" }}
        pausedOverlay={
          <img
            src={
              typeof content.image === "string" &&
              (content.cardImage ?? content.image)
                ? content.cardImage ?? content.image
                : defaultCardImage
            }
            alt="sdsdsdsd"
          />
        }
        loadingOverlayWrapperClassName={css`
          /* Loading overlay has semi-transparent background so the player
              will be darkened while it's visible */
          background-color: rgba(0, 0, 0, 0.7);
          /* Center the spinner inside of the overlay */
          display: flex;
          justify-content: center;
          align-items: center;
        `}
        loadingOverlay={
          <div>
            <div
              className={css`
                width: 4em;
                height: 4em;
                border: 6px solid white;
                border-radius: 50%;
                border-color: white white transparent transparent;
                animation: ${loadingOverlaySpinnerAnimation} 1s linear infinite;
              `}
            />
          </div>
        }
        className="punishmentVideo"
        loop={true}
        unloadVideoOnPaused={false}
        muted={true}
        overlayTransitionDuration={500}
        disablePictureInPicture={false}
        disableRemotePlayback={false}
        sizingMode="overlay"
        preload="auto"
      ></HoverVideoPlayer>
    );
  };

  return (
    <main className="pageOuterWrapper">
      <Navbar />
      <div className="mainContentWrap">
        <MetaHead
          title={explorePageMeta.title}
          description={explorePageMeta.description}
          keywords={explorePageMeta.keywords}
          url="https://fantasyloser.com/draft-ideas"
        />

        <div className="bannerWrap punishmentBanner">
          <div className="wrapper">
            <h2>Draft Ideas Gallery</h2>
            <div className="bannerSubTxt">
              A legendary lineup of draft day ideas from wild setups to clever
              twists perfect for every kind of league.
            </div>
          </div>
        </div>
        <div className="contentArea">
          <div className="searchBlk">
            <div className="wrapper">
              <div className="searchFldWrap">
                <input
                  type="text"
                  placeholder="Search Draft Ideas"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <SearchPanel
                  embarrassmentLevel={embarrassmentLevel}
                  setEmbarrassmentLevel={setEmbarrassmentLevel}
                  riskLevel={riskLevel}
                  setRiskLevel={setRiskLevel}
                  longTermEffects={longTermEffects}
                  setLongTermEffects={setLongTermEffects}
                  setIsFilterApplied={setIsFilterApplied}
                  selectedFLRating={selectedFLRating}
                  setSelectedFLRating={setSelectedFLRating}
                  contentType="draftIdeas"
                />
              </div>
              {userRole === "admin" && (
                <button
                  onClick={handleReorderButtonClick}
                  className="btnStyle"
                  disabled={isSavingOrder}
                >
                  {isSavingOrder
                    ? "Reordering"
                    : isReordering
                    ? "Finish Reordering"
                    : "Reorder Draft Ideas"}
                  {isSavingOrder && <div className="btnSpinner animate-spin" />}
                </button>
              )}
            </div>
          </div>
          <div className="innerWrapper">
            <div className="leaderboardAdBlk">
              <AdUnit
                slot="2591317351"
                format="horizontal"
                client="ca-pub-2646617990321066"
                style={{ display: "block", width: "728px", maxHeight: "90px" }}
              />
            </div>
            <div
              className={`punishmentItems ${isListView ? "showListView" : ""}`}
            >
              <div className="sectionHeading">
                <strong>
                  Results:{" "}
                  {isFilterApplied || searchTerm
                    ? filteredCount
                    : !isSavingOrder
                    ? "All"
                    : ""}
                </strong>
                <ul className="viewOptions">
                  <li
                    className="gridView"
                    onClick={() => handleViewChange("grid")}
                  >
                    Grid
                  </li>
                  <li
                    className="listView"
                    onClick={() => handleViewChange("list")}
                  >
                    List
                  </li>
                </ul>
              </div>
              {isSwitchingView || isSavingOrder ? (
                <div className="flex justify-center items-center h-full w-full">
                  <div className="w-8 h-8 border-4 border-[#18f7b1] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <ul className="productList">
                  {filteredDraftIdeas
                    .slice(0, displayCount)
                    .map((item, index) => {
                      const level = extractLevel(item.flRating);
                      const topPosition = extractPosition(item.order ?? -1);
                      const bgImage = !isListView
                        ? `url(${
                            item.cardImage || item.image || defaultCardImage
                          })`
                        : "";

                      return (
                        <li
                          key={item.id}
                          style={{ backgroundImage: bgImage }}
                          onClick={() => !isReordering && handleCardClick(item)}
                        >
                          <div className="listLeftCont">
                            {!isListView && item.videoUrl
                              ? renderVideoBlock(item)
                              : null}
                            {topPosition && (
                              <div className={topPosition.replace(/_/g, "")}>
                                Top {topPosition.split("_")[1]}
                              </div>
                            )}
                            <div className="favItemHeading">{item.title}</div>
                            <div className={`levelBlk badge-${level}`}>
                              Level {level}
                            </div>
                          </div>
                          {isListView && (
                            <div className="descText">{item.subtitle}</div>
                          )}
                          {isReordering && userRole === "admin" && (
                            <div className="moveControl">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReorder(item.id, "up");
                                }}
                                className="moveItem disabled:opacity-50"
                                disabled={index === 0}
                              >
                                <ArrowUp size={20} color="black" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReorder(item.id, "down");
                                }}
                                className="moveItem disabled:opacity-50"
                                disabled={
                                  index === filteredDraftIdeas.length - 1
                                }
                              >
                                <ArrowDown size={20} color="black" />
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}

                  {filteredDraftIdeas.length > displayCount && (
                    <div
                      ref={loaderRef}
                      className="w-full flex justify-center items-center py-8"
                    >
                      {isLoadingMore ? (
                        <div className="w-8 h-8 border-4 border-[#18f7b1] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <div className="h-8" />
                      )}
                    </div>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
};
export default DraftIdeas;
